import { CreatorInfo } from "@/components/CreatorInfo";
import { ReceiptPayments } from "@/components/PaymentLinks";
import { PdfUploadDialog, PdfReviewSheet, PdfStatementHistory } from "@/components/BankPdfImport";
import { useAuth } from "@/lib/auth";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fmtDate, fmtDateTime, fmtMoney, toMinor } from "@/lib/format";
import { toast } from "sonner";
import { Upload, Plus, FileDown } from "lucide-react";
import { SentEmailChip, SentEmailBlock } from "@/components/SentEmail";
import { EmailSendButton } from "@/components/EmailSendButton";
import { FlagButton, FlagBadge } from "@/components/FlagButton";
import { RowDeleteButton } from "@/components/RowDeleteButton";

const STATUS = {
  unverified: "bg-amber-50 text-amber-700 border-amber-200",
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Receipts() {
  const { user } = useAuth();
  const canImportPdf = ["superadmin", "accounts", "operations"].includes(user?.role);
  const [openPdf, setOpenPdf] = useState(false), [pdfBatchId, setPdfBatchId] = useState(null), [pdfReload, setPdfReload] = useState(0);
  const [receipts, setReceipts] = useState([]);
  const [imports, setImports] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailed, setEmailed] = useState("");
  const [searchParams] = useSearchParams();
  const linkedReceiptId = searchParams.get("id");
  const linkedStatementId = searchParams.get("statement");
  useEffect(() => { if (linkedStatementId) { setSelected(null); setPdfBatchId(linkedStatementId); } }, [linkedStatementId]);
  useEffect(() => {
    if (!linkedReceiptId) return;
    setPdfBatchId(null);
    api.get(`/receipts/${linkedReceiptId}`).then(({ data }) => setSelected(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [linkedReceiptId]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (emailed) params.emailed = emailed;
    const [r, i, s, c] = await Promise.all([
      api.get("/receipts", { params }), api.get("/receipts/bank-imports"),
      api.get("/sales"), api.get("/customers"),
    ]);
    setReceipts(r.data); setImports(i.data); setSales(s.data); setCustomers(c.data);
    setLoading(false);
  }, [emailed]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <PageHeader title="Receipts" subtitle="Record, verify, and allocate incoming money. Review bank PDFs or import CSVs.">
        <Button variant="outline" onClick={() => setOpenImport(true)} data-testid="bank-import-button"><Upload className="w-4 h-4 mr-1.5" /> Import CSV</Button>
        {canImportPdf && <Button variant="outline" onClick={() => setOpenPdf(true)} data-testid="bank-pdf-import-button"><Upload className="w-4 h-4 mr-1.5" /> Import bank PDF</Button>}
        <Button onClick={() => setOpenNew(true)} data-testid="new-receipt-button"><Plus className="w-4 h-4 mr-1.5" /> New receipt</Button>
      </PageHeader>
      <PageBody>
        <Tabs defaultValue="receipts">
          <TabsList className="mb-4">
            <TabsTrigger value="receipts" data-testid="tab-receipts">Receipts</TabsTrigger>
            <TabsTrigger value="imports" data-testid="tab-imports">CSV history</TabsTrigger>
            <TabsTrigger value="pdf-statements" data-testid="tab-pdf-statements">PDF statements</TabsTrigger>
          </TabsList>
          <TabsContent value="receipts">
            <div className="flex items-center gap-3 mb-3">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Emailed</Label>
              <Select value={emailed || "all"} onValueChange={(v) => setEmailed(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40" data-testid="receipt-filter-emailed"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Emailed</SelectItem>
                  <SelectItem value="no">Not emailed</SelectItem>
                </SelectContent>
              </Select>
              {emailed && <Button variant="ghost" size="sm" onClick={() => setEmailed("")}>Clear</Button>}
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>OR #</TableHead>
                    <TableHead>Remitter</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead className="text-right">Unallocated</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last emailed</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>}
                  {!loading && receipts.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-10">No receipts yet.</TableCell></TableRow>}
                  {receipts.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)} data-testid={`receipt-row-${r.id}`}>
                      <TableCell className="text-xs font-mono">{fmtDate(r.received_date)}</TableCell>
                      <TableCell className="text-xs font-mono" data-testid={`receipt-or-number-${r.id}`}>
                        {r.or_number || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell>{r.remitter_name}</TableCell>
                      <TableCell className="text-xs">{r.provider}</TableCell>
                      <TableCell className="text-xs font-mono">{r.bank_ref || r.provider_txn_id || "—"}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtMoney(r.amount_minor, r.currency)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-emerald-700">{fmtMoney(r.allocated_minor, r.currency)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmtMoney(r.unallocated_minor, r.currency)}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS[r.status]}>{r.status.toUpperCase()}</Badge></TableCell>
                      <TableCell><SentEmailChip record={r} testId={`receipt-last-emailed-${r.id}`} /></TableCell>
                      <TableCell className="w-10 py-1" onClick={(e) => e.stopPropagation()}>
                        <RowDeleteButton
                          endpoint={`/receipts/${r.id}`}
                          label={`receipt ${r.provider_txn_id || r.bank_ref || r.id.slice(0, 8)}`}
                          onDeleted={load}
                          testId={`receipt-delete-${r.id}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="imports">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>File</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Rows</TableHead><TableHead className="text-right">Imported</TableHead><TableHead className="text-right">Duplicates</TableHead></TableRow></TableHeader>
                <TableBody>
                  {imports.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No imports yet.</TableCell></TableRow>}
                  {imports.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="text-xs font-mono">{b.filename}</TableCell>
                      <TableCell className="text-xs font-mono">{fmtDateTime(b.created_at)}</TableCell>
                      <TableCell className="text-right font-mono">{b.row_count}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{b.imported}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500">{b.duplicates}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="pdf-statements"><PdfStatementHistory onReview={setPdfBatchId} reloadKey={pdfReload} /></TabsContent>
        </Tabs>
      </PageBody>

      <PdfUploadDialog open={openPdf} onOpenChange={setOpenPdf} onReady={(id) => { setOpenPdf(false); setPdfBatchId(id); setPdfReload((n) => n + 1); }} />
      <PdfReviewSheet batchId={pdfBatchId} onClose={() => setPdfBatchId(null)} onChanged={() => { setPdfReload((n) => n + 1); load(); }} />
      <NewReceiptDialog open={openNew} onOpenChange={setOpenNew} customers={customers} onCreated={() => { setOpenNew(false); load(); }} />
      <ImportDialog open={openImport} onOpenChange={setOpenImport} onDone={() => { setOpenImport(false); load(); }} />

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="min-w-[560px] overflow-y-auto" data-testid="receipt-detail-sheet">
          <SheetHeader><SheetTitle className="font-mono text-sm">{selected?.or_number || selected?.remitter_name || "Payment details"}</SheetTitle><SheetDescription>Verification and allocation details for this payment.</SheetDescription></SheetHeader>
          {selected && <ReceiptDetail receipt={selected} sales={sales} onChanged={async () => { await load(); const fresh = (await api.get("/receipts")).data.find((r) => r.id === selected.id); setSelected(fresh || null); }} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NewReceiptDialog({ open, onOpenChange, customers, onCreated }) {
  const [form, setForm] = useState({ remitter_name: "", amount: "", currency: "MYR", received_date: new Date().toISOString().slice(0, 10), provider: "bank_transfer", provider_txn_id: "", bank_ref: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.remitter_name || !form.amount || !form.received_date) { toast.error("Complete required fields"); return; }
    setSaving(true);
    try {
      await api.post("/receipts", {
        remitter_name: form.remitter_name,
        amount_minor: toMinor(form.amount), currency: form.currency,
        received_date: form.received_date, provider: form.provider,
        provider_txn_id: form.provider_txn_id || null,
        bank_ref: form.bank_ref || null,
      });
      toast.success("Receipt captured");
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record a receipt</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider">Remitter name</Label>
            <Input value={form.remitter_name} onChange={(e) => setForm({ ...form, remitter_name: e.target.value })} data-testid="receipt-remitter" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider">Amount</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="receipt-amount" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MYR">MYR</SelectItem><SelectItem value="SGD">SGD</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider">Received date</Label>
              <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider">Provider</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="payex">Payex</SelectItem>
                  <SelectItem value="chip">CHIP</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs uppercase tracking-wider">Bank / provider reference</Label>
            <Input value={form.bank_ref} onChange={(e) => setForm({ ...form, bank_ref: e.target.value })} data-testid="receipt-ref" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="receipt-submit">{saving ? "Saving…" : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Pick a CSV file"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts/bank-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} of ${data.row_count} rows (${data.duplicates} duplicates)`);
      setFile(null);
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Import bank CSV</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>Upload an OCTO Biz-style CSV. Recognised columns (case-insensitive): <code className="text-xs font-mono">date, amount, currency, description, reference, remitter</code>.</p>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="bank-file" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="bank-import-submit">{saving ? "Importing…" : "Import"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDetail({ receipt, sales, onChanged }) {
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try { await api.post(`/receipts/${receipt.id}/verify`); toast.success("Verified"); await onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <div className="mt-6 space-y-5 text-sm">
    <Badge variant="outline" className={STATUS[receipt.status]} data-testid="receipt-status">{receipt.status.toUpperCase()}</Badge>
    <div className="grid grid-cols-2 gap-3 text-xs">
      {receipt.or_number && (
        <Info label="Official Receipt No"><span className="font-mono font-semibold" data-testid="receipt-or-number">{receipt.or_number}</span></Info>
      )}
      <Info label="Amount"><span className="font-mono" data-testid="receipt-total">{fmtMoney(receipt.amount_minor, receipt.currency)}</span></Info>
      <Info label="Received">{fmtDate(receipt.received_date)}</Info>
      <Info label="Provider">{receipt.provider}</Info>
      <Info label="Ref">{receipt.bank_ref || receipt.provider_txn_id || "—"}</Info>
      <Info label="Allocated"><span className="font-mono text-emerald-700" data-testid="receipt-allocated">{fmtMoney(receipt.allocated_minor, receipt.currency)}</span></Info>
      <Info label="Unallocated"><span className="font-mono" data-testid="receipt-unallocated">{fmtMoney(receipt.unallocated_minor, receipt.currency)}</span></Info>
    </div>
    <CreatorInfo record={receipt} kind="receipt" />
    {receipt.bank_pdf_batch_id && <div className="border rounded p-3 text-xs space-y-1" data-testid="receipt-bank-pdf-source"><Link className="text-blue-700 underline" to={`/receipts?statement=${receipt.bank_pdf_batch_id}`} data-testid="receipt-bank-pdf-link">View source bank statement</Link><p>{receipt.bank_account_masked} · Reviewed PDF deposit. No automatic invoice or allocation was applied.</p></div>}
    {receipt.gateway_transaction_id && <div className="border rounded p-3 text-xs space-y-1" data-testid="receipt-gateway-source"><Link className="text-blue-700 underline" to={`/transactions?id=${receipt.gateway_transaction_id}`} data-testid="receipt-gateway-transaction-link">View gateway transaction</Link>{receipt.gateway_mode === "test" && <p className="text-amber-800 font-semibold" data-testid="receipt-gateway-test-warning">TEST payment — not live revenue</p>}<p>Created explicitly by staff; verification and allocation follow the existing Accounts workflow.</p></div>}
    {receipt.status === "unverified" && <Button size="sm" onClick={verify} disabled={busy} data-testid="verify-receipt">Verify receipt</Button>}
    <div className="flex flex-wrap gap-2">
      {receipt.status !== "rejected" && (
        <EmailSendButton
          resource="receipts"
          id={receipt.id}
          defaultTo={receipt.remitter_email || ""}
          label="receipt"
          triggerTestId="send-receipt"
          onSent={onChanged}
        />
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/receipts/${receipt.id}/pdf`, "_blank", "noopener")}
        data-testid="receipt-download-pdf"
      >
        <FileDown className="w-4 h-4 mr-1.5" />
        {receipt.or_number ? "Official Receipt PDF" : "Preview PDF"}
      </Button>
      <FlagButton resource="receipts" id={receipt.id} flag={receipt.flag} onChanged={onChanged} testId="flag-receipt-button" />
    </div>
    <ReceiptPayments receipt={receipt} sales={sales} onChanged={onChanged} />
    <SentEmailBlock record={receipt} testId="receipt-sent-email-block" />
  </div>;
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900 text-sm">{children}</div>
    </div>
  );
}
